
import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Review } from './ReviewSection';

interface ReviewListProps {
  reviews: Review[];
  loading: boolean;
  sortBy: 'newest' | 'highest' | 'lowest' | 'helpful';
  onSortChange: (sort: 'newest' | 'highest' | 'lowest' | 'helpful') => void;
  userId?: string;
}

export default function ReviewList({
  reviews,
  loading,
  sortBy,
  onSortChange,
  userId,
}: ReviewListProps) {
  const [helpfulIds, setHelpfulIds] = useState<Set<number>>(new Set());
  const [visibleCount, setVisibleCount] = useState(5);
  const [error, setError] = useState<string | null>(null);

  const handleHelpful = async (reviewId: number) => {
    if (helpfulIds.has(reviewId)) return;

    // Optimistically update UI
    setHelpfulIds((prev) => new Set(prev).add(reviewId));

    try {
      const { error: dbError } = await supabase
        .from('reviews')
        .update({
          helpful_count:
            (reviews.find((r) => r.id === reviewId)?.helpful_count || 0) + 1,
        })
        .eq('id', reviewId);

      if (dbError) throw dbError;
    } catch (e) {
      // Revert optimistic update on failure
      setHelpfulIds((prev) => {
        const copy = new Set(prev);
        copy.delete(reviewId);
        return copy;
      });
      setError('Unable to register helpful vote. Please try again.');
      console.error(e);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`${
          i < rating ? 'ri-star-fill text-[#2E7D32]' : 'ri-star-line text-gray-300'
        } text-sm`}
      />
    ));
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse p-6 border border-gray-100 rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="h-4 bg-gray-200 rounded w-24" />
            </div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-full mb-1" />
            <div className="h-3 bg-gray-200 rounded w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Sort Controls */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">
          Showing {Math.min(visibleCount, reviews.length)} of {reviews.length} reviews
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as ReviewListProps['sortBy'])}
            className="text-sm font-medium text-gray-700 bg-transparent border border-gray-200 rounded-lg px-3 py-1.5 cursor-pointer focus:outline-none focus:border-[#2E7D32]"
          >
            <option value="newest">Most Recent</option>
            <option value="helpful">Most Helpful</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
          </select>
        </div>
      </div>

      {/* Reviews */}
      {reviews.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-200 rounded-xl">
          <i className="ri-chat-smile-3-line text-4xl text-gray-300 mb-3 block"></i>
          <p className="text-gray-500 text-sm mb-1">No reviews yet for this filter</p>
          <p className="text-gray-400 text-xs">Be the first to share your experience!</p>
        </div>
      ) : (
        <div className="space-y-0">
          {reviews.slice(0, visibleCount).map((review, index) => {
            const isOwn = userId && review.user_id === userId;
            return (
              <div
                key={review.id}
                className={`py-6 ${
                  index < Math.min(visibleCount, reviews.length) - 1
                    ? 'border-b border-gray-100'
                    : ''
                }`}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#E8F5E9] flex items-center justify-center text-sm font-semibold text-[#2E7D32]">
                      {review.author_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">
                          {review.author_name}
                        </span>
                        {review.verified_purchase && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#E8F5E9] text-[#2E7D32] text-[10px] font-semibold rounded-full">
                            <i className="ri-verified-badge-fill text-xs"></i>
                            Verified
                          </span>
                        )}
                        {isOwn && (
                          <span className="text-[10px] text-gray-400 font-medium">
                            (You)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{timeAgo(review.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">{renderStars(review.rating)}</div>
                </div>

                {/* Content */}
                <h5 className="text-sm font-semibold text-gray-900 mb-1.5">{review.title}</h5>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{review.body}</p>

                {/* Footer */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => handleHelpful(review.id)}
                    disabled={helpfulIds.has(review.id)}
                    className={`flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${
                      helpfulIds.has(review.id)
                        ? 'text-[#2E7D32] font-medium'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    <i
                      className={`${
                        helpfulIds.has(review.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'
                      } w-3.5 h-3.5 flex items-center justify-center`}
                    ></i>
                    Helpful ({(review.helpful_count || 0) + (helpfulIds.has(review.id) ? 1 : 0)})
                  </button>
                  <span className="text-gray-200">|</span>
                  <span className="text-xs text-gray-400">{formatDate(review.created_at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {visibleCount < reviews.length && (
        <div className="text-center mt-8">
          <button
            onClick={() => setVisibleCount((prev) => prev + 5)}
            className="px-8 py-2.5 border border-gray-200 text-sm font-medium text-gray-700 rounded-lg hover:border-[#2E7D32] hover:text-[#2E7D32] transition-all cursor-pointer whitespace-nowrap"
          >
            Load More Reviews
          </button>
        </div>
      )}

      {/* Optional error display */}
      {error && <p className="mt-4 text-center text-sm text-red-600">{error}</p>}
    </div>
  );
}
